import type { ComponentProps } from "react";
import {
    AddButton as AddButtonBase,
    BackButton as BackButtonBase,
    CancelButton as CancelButtonBase,
    ConfirmButton as ConfirmButtonBase,
    ContinueButton as ContinueButtonBase,
    DeleteButton as DeleteButtonBase,
    DoneButton as DoneButtonBase,
    DownloadButton as DownloadButtonBase,
    EditButton as EditButtonBase,
    NextButton as NextButtonBase,
    RetryButton as RetryButtonBase,
    SaveButton as SaveButtonBase,
    SendButton as SendButtonBase,
    SkipButton as SkipButtonBase,
    StopButton as StopButtonBase,
    SubmitButton as SubmitButtonBase,
} from "@patient-watch/forms";
import { useActionCopy } from "./actionCopy";

type AddButtonProps = ComponentProps<typeof AddButtonBase>;
type BackButtonProps = ComponentProps<typeof BackButtonBase>;
type CancelButtonProps = ComponentProps<typeof CancelButtonBase>;
type ConfirmButtonProps = ComponentProps<typeof ConfirmButtonBase>;
type ContinueButtonProps = ComponentProps<typeof ContinueButtonBase>;
type DeleteButtonProps = ComponentProps<typeof DeleteButtonBase>;
type DoneButtonProps = ComponentProps<typeof DoneButtonBase>;
type DownloadButtonProps = ComponentProps<typeof DownloadButtonBase>;
type EditButtonProps = ComponentProps<typeof EditButtonBase>;
type NextButtonProps = ComponentProps<typeof NextButtonBase>;
type RetryButtonProps = ComponentProps<typeof RetryButtonBase>;
type SaveButtonProps = ComponentProps<typeof SaveButtonBase>;
type SendButtonProps = ComponentProps<typeof SendButtonBase>;
type SkipButtonProps = ComponentProps<typeof SkipButtonBase>;
type StopButtonProps = ComponentProps<typeof StopButtonBase>;
type SubmitButtonProps = ComponentProps<typeof SubmitButtonBase>;

/** Localised Add with plus icon (create flows, add entry). */
export function AddActionButton({ label, loadingText, ...props }: AddButtonProps) {
    const copy = useActionCopy();
    return <AddButtonBase label={label ?? copy.add} loadingText={loadingText ?? copy.adding} {...props} />;
}

/** Localised Delete with trash icon (destructive dialog footers). */
export function DeleteActionButton({ label, loadingText, ...props }: DeleteButtonProps) {
    const copy = useActionCopy();
    return <DeleteButtonBase label={label ?? copy.delete} loadingText={loadingText ?? copy.deleting} {...props} />;
}

/** Localised Cancel with close icon (dialogs, dismiss actions). */
export function CancelActionButton({ label, loadingText, ...props }: CancelButtonProps) {
    const copy = useActionCopy();
    return <CancelButtonBase label={label ?? copy.cancel} loadingText={loadingText ?? copy.cancelling} {...props} />;
}

/** Localised Confirm with check icon (confirmation dialogs). */
export function ConfirmActionButton({ label, loadingText, ...props }: ConfirmButtonProps) {
    const copy = useActionCopy();
    return <ConfirmButtonBase label={label ?? copy.confirm} loadingText={loadingText ?? copy.confirming} {...props} />;
}

/** Localised Continue with arrow icon (onboarding, auth, setup flows). */
export function ContinueActionButton({ label, loadingText, ...props }: ContinueButtonProps) {
    const copy = useActionCopy();
    return (
        <ContinueButtonBase label={label ?? copy.continue} loadingText={loadingText ?? copy.continuing} {...props} />
    );
}

/** Localised Done with check icon (exit edit mode). */
export function DoneActionButton({ label, loadingText, ...props }: DoneButtonProps) {
    const copy = useActionCopy();
    return <DoneButtonBase label={label ?? copy.done} loadingText={loadingText ?? copy.finishing} {...props} />;
}

/** Localised Download with download icon (export, PDF). */
export function DownloadActionButton({ label, loadingText, ...props }: DownloadButtonProps) {
    const copy = useActionCopy();
    return (
        <DownloadButtonBase label={label ?? copy.download} loadingText={loadingText ?? copy.downloading} {...props} />
    );
}

/** Localised Edit with pencil icon (open edit mode or dialog). */
export function EditActionButton({ label, loadingText, ...props }: EditButtonProps) {
    const copy = useActionCopy();
    return <EditButtonBase label={label ?? copy.edit} loadingText={loadingText ?? copy.editing} {...props} />;
}

/** Localised Next with arrow icon (onboarding, multi-step forms). */
export function NextActionButton({ label, loadingText, ...props }: NextButtonProps) {
    const copy = useActionCopy();
    return <NextButtonBase label={label ?? copy.next} loadingText={loadingText ?? label ?? copy.next} {...props} />;
}

/** Localised Back with leading arrow icon (multi-step forms, secondary footer action). */
export function BackActionButton({ label, loadingText, ...props }: BackButtonProps) {
    const copy = useActionCopy();
    return <BackButtonBase label={label ?? copy.back} loadingText={loadingText ?? copy.goingBack} {...props} />;
}

/** Localised Retry with rotate icon (error recovery). */
export function RetryActionButton({ label, loadingText, ...props }: RetryButtonProps) {
    const copy = useActionCopy();
    return <RetryButtonBase label={label ?? copy.retry} loadingText={loadingText ?? copy.retrying} {...props} />;
}

/** Localised Save with save icon (dialogs, edit forms). */
export function SaveActionButton({ label, loadingText, ...props }: SaveButtonProps) {
    const copy = useActionCopy();
    return <SaveButtonBase label={label ?? copy.save} loadingText={loadingText ?? copy.saving} {...props} />;
}

/** Localised Send with send icon (invites, verification, outbound actions). */
export function SendActionButton({ label, loadingText, ...props }: SendButtonProps) {
    const copy = useActionCopy();
    return <SendButtonBase label={label ?? copy.send} loadingText={loadingText ?? copy.sending} {...props} />;
}

/** Localised Skip with skip icon (onboarding, optional steps). */
export function SkipActionButton({ label, loadingText, ...props }: SkipButtonProps) {
    const copy = useActionCopy();
    return <SkipButtonBase label={label ?? copy.skip} loadingText={loadingText ?? copy.skipping} {...props} />;
}

/** Localised Stop with stop icon (end diary, stop collecting). */
export function StopActionButton({ label, loadingText, ...props }: StopButtonProps) {
    const copy = useActionCopy();
    return <StopButtonBase label={label ?? copy.stop} loadingText={loadingText ?? copy.stopping} {...props} />;
}

/** Localised Submit with check icon (questionnaires, final form step). */
export function SubmitActionButton({ label, loadingText, ...props }: SubmitButtonProps) {
    const copy = useActionCopy();
    return <SubmitButtonBase label={label ?? copy.submit} loadingText={loadingText ?? copy.submitting} {...props} />;
}
